import React, { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import {
  FiSend,
  FiSearch,
  FiInbox,
  FiChevronLeft,
  FiSmile,
  FiPaperclip,
  FiX,
} from "react-icons/fi";
import EmojiPicker from "emoji-picker-react";
import {
  postRequestAuthenticated,
  postRequestAuthenticatedWithFile,
} from "../../../services/rest";
import socket, { connectSocketWithUser } from "../../../utils/socket";
import { SkeletonList, SkeletonBar } from "../widgets/Skeleton";

const initial = (s) => (s || "?").charAt(0).toUpperCase();
const fmtTime = (d) => {
  if (!d) return "";
  try {
    return new Date(d).toLocaleString([], {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (e) {
    return "";
  }
};

const Avatar = ({ user, size = "h-9 w-9" }) => {
  if (user?.logo)
    return (
      <img src={user.logo} alt="" className={`${size} rounded-full object-cover`} />
    );
  return (
    <span
      className={`${size} flex flex-shrink-0 items-center justify-center rounded-full text-sm font-bold text-gray-900`}
      style={{ background: user?.color_logo || "#C4C4C4" }}
    >
      {initial(user?.name || user?.username)}
    </span>
  );
};

const InboxTab = () => {
  const myData = useSelector((s) => s.myData);
  const [convos, setConvos] = useState([]);
  const [loadingConvos, setLoadingConvos] = useState(false);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [q, setQ] = useState("");
  const [showEmoji, setShowEmoji] = useState(false);
  const [files, setFiles] = useState([]); // attached File objects
  const endRef = useRef(null);
  const fileInputRef = useRef(null);
  // Track the open conversation so the (stable) socket handler reads it fresh.
  const activeUserIdRef = useRef(null);
  useEffect(() => {
    activeUserIdRef.current = active?.user?._id || null;
  }, [active]);

  const mediaType = (f) =>
    f.type?.startsWith("image/")
      ? "image"
      : f.type?.startsWith("video/")
      ? "video"
      : "document";

  const loadConvos = () => {
    setLoadingConvos(true);
    postRequestAuthenticated("/fetch/brand/conversations", {})
      .then((res) => {
        if (res?.success) setConvos(res.conversations || []);
      })
      .finally(() => setLoadingConvos(false));
  };

  useEffect(() => {
    loadConvos();
  }, []);

  // Live delivery of member "Talk to us" messages (embed -> admin inbox).
  // The owner's socket joins room <ownerId>; create_brand_chat emits dm_message
  // there. Without this the owner had to refresh to see new member messages.
  useEffect(() => {
    if (!myData?._id) return;
    connectSocketWithUser(myData._id);
    const onDM = (msg) => {
      const senderId = String(msg?.sender?._id || msg?.sender || "");
      if (!senderId || senderId === String(myData._id)) return; // ignore own echo
      // Append straight into the open conversation if it belongs to it.
      if (senderId === String(activeUserIdRef.current)) {
        setMessages((m) =>
          m.some((x) => String(x._id) === String(msg._id)) ? m : [...m, msg]
        );
      }
      // Refresh sidebar ordering / last message / unread badge.
      loadConvos();
    };
    socket.on("dm_message", onDM);
    return () => socket.off("dm_message", onDM);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myData?._id]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const openConvo = (c) => {
    setActive(c);
    setMessages([]);
    setText("");
    setFiles([]);
    setShowEmoji(false);
    setLoadingMsgs(true);
    postRequestAuthenticated("/fetch/brand/chats", { user_id: c.user._id })
      .then((res) => {
        if (res?.success) setMessages(res.chats || []);
        // clear the unread badge locally
        setConvos((prev) =>
          prev.map((x) =>
            x.user._id === c.user._id ? { ...x, unread: 0 } : x
          )
        );
      })
      .finally(() => setLoadingMsgs(false));
  };

  const send = () => {
    if ((!text.trim() && files.length === 0) || !active || sending) return;
    setSending(true);
    setShowEmoji(false);
    const fd = new FormData();
    fd.append("username", active.user.username);
    fd.append("content", text.trim());
    fd.append("media", JSON.stringify(files.map((f) => ({ type: mediaType(f) }))));
    fd.append("links", "[]");
    fd.append("replyTo", "null");
    files.forEach((f) => fd.append("files", f));
    postRequestAuthenticatedWithFile("/create/brand/chat", fd)
      .then((res) => {
        if (res?.success && res.chat) {
          setMessages((m) => [...m, res.chat]);
          setText("");
          setFiles([]);
          loadConvos();
        }
      })
      .finally(() => setSending(false));
  };

  const shown = convos.filter(
    (c) =>
      !q.trim() ||
      `${c.user.name || ""} ${c.user.username || ""}`
        .toLowerCase()
        .includes(q.toLowerCase())
  );

  return (
    <div className="flex h-[calc(100vh-56px)] w-full">
      {/* Conversations sidebar */}
      <div
        className={`${
          active ? "hidden sm:flex" : "flex"
        } w-full flex-col border-r border-theme-chatDivider sm:w-80`}
      >
        <div className="border-b border-theme-chatDivider p-4">
          <h1 className="text-lg font-semibold text-theme-secondaryText">
            Talk to us
          </h1>
          <p className="text-xs text-theme-placeholder">
            Conversations from your members.
          </p>
          <div className="mt-3 flex items-center gap-2 rounded-lg border border-theme-chatDivider bg-theme-tertiaryBackground px-3 py-1.5">
            <FiSearch size={14} className="text-theme-placeholder" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search people…"
              className="w-full bg-transparent text-sm text-theme-secondaryText outline-none placeholder:text-theme-placeholder"
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto custom-scrollbar">
          {loadingConvos ? (
            <div className="p-3">
              <SkeletonList rows={6} />
            </div>
          ) : shown.length === 0 ? (
            <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
              <FiInbox size={26} className="mb-3 text-theme-placeholder" />
              <p className="text-sm text-theme-secondaryText">No messages yet</p>
              <p className="text-xs text-theme-placeholder">
                When members use "Talk to us", their chats appear here.
              </p>
            </div>
          ) : (
            shown.map((c) => (
              <button
                key={c.roomId}
                onClick={() => openConvo(c)}
                className={`flex w-full items-center gap-3 border-b border-theme-chatDivider/60 px-4 py-3 text-left transition-colors hover:bg-theme-tertiaryBackground ${
                  active?.user._id === c.user._id
                    ? "bg-theme-tertiaryBackground"
                    : ""
                }`}
              >
                <Avatar user={c.user} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-sm font-medium text-theme-secondaryText">
                      {c.user.name || c.user.username}
                    </span>
                    {c.unread > 0 && (
                      <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-theme-accent px-1.5 text-[11px] font-semibold text-theme-accentText">
                        {c.unread}
                      </span>
                    )}
                  </div>
                  <p className="truncate text-xs text-theme-placeholder">
                    {c.lastMessage.fromMe ? "You: " : ""}
                    {c.lastMessage.content ||
                      (c.lastMessage.hasMedia ? "Attachment" : "")}
                  </p>
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Conversation view */}
      <div className={`${active ? "flex" : "hidden sm:flex"} flex-1 flex-col`}>
        {!active ? (
          <div className="flex h-full flex-col items-center justify-center text-center text-theme-placeholder">
            <FiInbox size={30} className="mb-3" />
            <p className="text-sm">Select a conversation</p>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 border-b border-theme-chatDivider p-3">
              <button
                onClick={() => setActive(null)}
                className="flex h-8 w-8 items-center justify-center rounded-full text-theme-secondaryText hover:bg-theme-tertiaryBackground sm:hidden"
              >
                <FiChevronLeft size={18} />
              </button>
              <Avatar user={active.user} size="h-8 w-8" />
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-theme-secondaryText">
                  {active.user.name || active.user.username}
                </p>
                {active.user.username && (
                  <p className="truncate text-xs text-theme-placeholder">
                    @{active.user.username}
                  </p>
                )}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto custom-scrollbar bg-theme-secondaryBackground p-4">
              {loadingMsgs ? (
                <SkeletonBar className="mx-auto h-3 w-24" />
              ) : messages.length === 0 ? (
                <p className="text-center text-sm text-theme-placeholder">
                  No messages in this conversation yet.
                </p>
              ) : (
                <div className="flex flex-col gap-2">
                  {messages.map((m) => {
                    const mine =
                      String(m.sender?._id || m.sender) ===
                      String(myData?._id);
                    return (
                      <div
                        key={m._id}
                        className={`flex ${
                          mine ? "justify-end" : "justify-start"
                        }`}
                      >
                        <div
                          className={`max-w-[75%] rounded-2xl px-3.5 py-2 text-sm ${
                            mine
                              ? "bg-theme-secondaryText text-theme-primaryBackground"
                              : "bg-theme-tertiaryBackground text-theme-secondaryText"
                          }`}
                        >
                          {m.content && (
                            <p className="whitespace-pre-wrap break-words">
                              {m.content}
                            </p>
                          )}
                          {(m.media || []).map((md, i) =>
                            md.type === "image" ? (
                              <img
                                key={i}
                                src={md.url}
                                alt=""
                                className="mt-1 max-h-48 rounded-lg object-cover"
                              />
                            ) : (
                              <a
                                key={i}
                                href={md.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mt-1 block underline"
                              >
                                {md.name || "Attachment"}
                              </a>
                            )
                          )}
                          <p
                            className={`mt-0.5 text-[10px] ${
                              mine
                                ? "text-theme-primaryBackground/70"
                                : "text-theme-placeholder"
                            }`}
                          >
                            {fmtTime(m.createdAt)}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={endRef} />
                </div>
              )}
            </div>

            <div className="relative border-t border-theme-chatDivider p-3">
              {/* attachment previews */}
              {files.length > 0 && (
                <div className="mb-2 flex flex-wrap gap-2">
                  {files.map((f, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-1.5 rounded-lg border border-theme-chatDivider bg-theme-tertiaryBackground px-2 py-1 text-xs text-theme-secondaryText"
                    >
                      <span className="max-w-[140px] truncate">{f.name}</span>
                      <button
                        onClick={() =>
                          setFiles((prev) => prev.filter((_, idx) => idx !== i))
                        }
                        className="text-theme-placeholder hover:text-theme-error"
                      >
                        <FiX size={13} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {showEmoji && (
                <div className="absolute bottom-16 left-3 z-20">
                  <EmojiPicker
                    onEmojiClick={(e) => setText((t) => t + e.emoji)}
                    skinTonesDisabled
                    searchDisabled
                    height={320}
                    width={300}
                    theme={
                      document.documentElement.classList.contains("dark")
                        ? "dark"
                        : "light"
                    }
                  />
                </div>
              )}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowEmoji((v) => !v)}
                  className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-theme-emptyEvent hover:bg-theme-tertiaryBackground"
                  title="Emoji"
                >
                  <FiSmile size={18} />
                </button>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-theme-emptyEvent hover:bg-theme-tertiaryBackground"
                  title="Attach"
                >
                  <FiPaperclip size={18} />
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,video/*"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    setFiles((prev) => [
                      ...prev,
                      ...Array.from(e.target.files || []),
                    ]);
                    e.target.value = "";
                  }}
                />
                <input
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onClick={() => setShowEmoji(false)}
                  onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && send()}
                  placeholder="Type a reply…"
                  className="flex-1 rounded-full border border-theme-chatDivider bg-theme-tertiaryBackground px-4 py-2.5 text-sm text-theme-secondaryText outline-none placeholder:text-theme-placeholder"
                />
                <button
                  onClick={send}
                  disabled={(!text.trim() && files.length === 0) || sending}
                  className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-theme-accent text-theme-accentText disabled:opacity-50"
                >
                  <FiSend size={16} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default InboxTab;
